import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Container from './Container.jsx';
import Button from './Button.jsx';
import { LanguageToggle } from '../../navigation';
import { useTranslation } from '../../i18n/hooks';
import { useSubscription } from '../../subscription';
import './Header.css';

/**
 * Header component with ArbiWin design system integration
 * Based on header_and_footer.html mockup structure
 *
 * @param {Object} props - Header properties
 * @param {React.ReactNode} props.logo - Logo component or image
 * @param {string} props.title - Site title
 * @param {Array} props.navigation - Navigation items array
 * @param {React.ReactNode} props.actions - Header action buttons
 * @param {boolean} props.sticky - Make header sticky
 * @param {boolean} props.transparent - Transparent background
 * @param {string} props.variant - Header variant: 'default', 'compact', 'landing'
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} Header component
 */
const Header = function Header({
  logo,
  title = 'ArbiWin.bg',
  navigation = [],
  actions,
  sticky = true,
  transparent = false,
  variant = 'default',
  className = '',
  testId,
  onHomeClick
}) {

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll for backdrop blur effect
  useEffect(function() {
    if (!sticky) return;

    const handleScroll = function() {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return function() {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [sticky]);

  const headerClasses = [
    'arbiwin-header',
    'arbiwin-header--' + variant,
    sticky ? 'arbiwin-header--sticky' : '',
    transparent ? 'arbiwin-header--transparent' : '',
    isScrolled ? 'arbiwin-header--scrolled' : '',
    isMenuOpen ? 'arbiwin-header--menu-open' : '',
    className
  ].filter(Boolean).join(' ');

  const toggleMenu = function() {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = function() {
    setIsMenuOpen(false);
  };

  // Close menu on escape key
  useEffect(function() {
    const handleKeyDown = function(event) {
      if (event.key === 'Escape' && isMenuOpen) {
        closeMenu();
      }
    };

    if (isMenuOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return function() {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isMenuOpen]);

  return (
    <header
      className={headerClasses}
      data-testid={testId || 'arbiwin-header'}
    >
      <Container size="full" padding="medium">
        <div className="arbiwin-header__content">
          {/* Logo and title */}
          <div className="arbiwin-header__brand">
            {logo && (
              <div className="arbiwin-header__logo">
                {logo}
              </div>
            )}
            <h1 className="arbiwin-header__title">
              {onHomeClick ? (
                <button 
                  className="arbiwin-header__title-link"
                  onClick={onHomeClick}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'inherit',
                    font: 'inherit',
                    cursor: 'pointer',
                    padding: 0,
                    textDecoration: 'none'
                  }}
                >
                  {title}
                </button>
              ) : (
                title
              )}
            </h1>
          </div>

          {/* Desktop navigation */}
          {navigation.length > 0 && (
            <nav className="arbiwin-header__nav arbiwin-header__nav--desktop">
              <ul className="arbiwin-header__nav-list">
                {navigation.map(function(item, index) {
                  return (
                    <li key={index} className="arbiwin-header__nav-item">
                      <a
                        href={item.href}
                        className="arbiwin-header__nav-link"
                        onClick={item.onClick}
                      >
                        {item.label}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </nav>
          )}

          {/* Header actions */}
          <div className="arbiwin-header__actions">
            {actions}

            {/* Mobile menu toggle - always show on mobile to access language toggle */}
            <button
              className="arbiwin-header__menu-toggle"
              onClick={toggleMenu}
              aria-expanded={isMenuOpen}
              aria-label="Toggle navigation menu"
              aria-controls="mobile-navigation"
            >
              <span className="arbiwin-header__menu-icon">
                <span />
                <span />
                <span />
              </span>
            </button>
          </div>
        </div>

        {/* Mobile navigation - always show to provide language toggle access */}
        <nav
          id="mobile-navigation"
          className="arbiwin-header__nav arbiwin-header__nav--mobile"
          aria-hidden={!isMenuOpen}
        >
          <ul className="arbiwin-header__nav-list">
            {navigation.map(function(item, index) {
              return (
                <li key={index} className="arbiwin-header__nav-item">
                  <a
                    href={item.href}
                    className="arbiwin-header__nav-link"
                    onClick={function(e) {
                      if (item.onClick) {
                        item.onClick(e);
                      }
                      closeMenu();
                    }}
                  >
                    {item.label}
                  </a>
                </li>
              );
            })}
            {/* Language toggle in mobile menu - always show */}
            <li className="arbiwin-header__nav-item arbiwin-header__nav-item--language">
              <div className="arbiwin-header__language-toggle arbiwin-header__language-toggle--mobile">
                <LanguageToggle 
                  variant="dropdown"
                  size="medium"
                  showFlags={true}
                  showLabels={true}
                  showCurrentLanguage={true}
                  position="top-right"
                />
              </div>
            </li>
          </ul>
        </nav>
      </Container>
    </header>
  );
};

/**
 * ArbiWinHeader component - Pre-configured header for ArbiWin
 * Based on the exact structure from header_and_footer.html
 *
 * @param {Object} props - ArbiWinHeader properties
 * @param {boolean} props.isAuthenticated - User authentication state
 * @param {Function} props.onLogin - Login handler
 * @param {Function} props.onRegister - Register handler
 * @param {Function} props.onLogout - Logout handler
 * @param {Function} props.onHomeClick - Home click handler
 * @param {Object} props.user - User object
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} ArbiWinHeader component
 */
const ArbiWinHeader = function ArbiWinHeader({
  isAuthenticated = false,
  onLogin,
  onRegister,
  onLogout,
  onHomeClick,
  onProfile,
  user,
  className = ''
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const subscription = useSubscription();
  const isActive = subscription?.computed?.isActive || false;
  const isLoadingSubscription = subscription?.loading?.status || false;

  const handleNavClick = (e, href) => {
    // If it's a hash link (starts with #), let it scroll naturally
    if (href.startsWith('#')) {
      return;
    }
    // Otherwise, use React Router navigation
    e.preventDefault();
    navigate(href);
  };

  // Build navigation array based on authentication and subscription status
  const navigation = [];
  
  if (isAuthenticated) {
    // Dashboard link for all authenticated users
    navigation.push({
      label: t('navigation.dashboard'),
      href: '/dashboard',
      onClick: (e) => handleNavClick(e, '/dashboard')
    });
    
    // "How to Subscribe" link for authenticated users without active subscription
    // Only show if subscription status is loaded and not active
    if (!isLoadingSubscription && !isActive) {
      navigation.push({
        label: t('navigation.howToSubscribe'),
        href: '/static/subscribe',
        onClick: (e) => handleNavClick(e, '/static/subscribe')
      });
    }
  }

  const logo = (
    <img
      src="./ArbiWinLogo.png"
      alt="ArbiWin Logo"
      className="arbiwin-header__logo-image"
      onError={function(e) {
        // Fallback if logo image fails to load
        e.target.style.display = 'none';
      }}
    />
  );

  const actions = isAuthenticated ? (
      <div className="arbiwin-header__auth-actions">
        <div className="arbiwin-header__language-toggle">
          <LanguageToggle 
            variant="dropdown"
            size="medium"
            showFlags={true}
            showLabels={true}
            showCurrentLanguage={true}
            position="bottom-right"
          />
        </div>
        <span className="arbiwin-header__user-name">
          {user?.name || user?.email}
        </span>
      {onProfile && (
        <Button
          variant="secondary"
          size="medium"
          onClick={onProfile}
          className="arbiwin-header__profile-btn"
        >
          {t('navigation.profile')}
        </Button>
      )}
      <Button
        variant="secondary"
        size="medium"
        onClick={onLogout}
      >
        {t('navigation.logout')}
      </Button>
    </div>
  ) : (
    <div className="arbiwin-header__auth-actions">
      <div className="arbiwin-header__language-toggle">
        <LanguageToggle 
          variant="dropdown"
          size="medium"
          showFlags={true}
          showLabels={true}
          showCurrentLanguage={true}
          position="bottom-right"
        />
      </div>
      <Button
        variant="secondary"
        size="medium"
        onClick={onLogin}
        className="arbiwin-header__login-btn"
      >
        {t('navigation.login')}
      </Button>
      <Button
        variant="primary"
        size="medium"
        onClick={onRegister}
      >
        {t('navigation.register')}
      </Button>
    </div>
  );

  return (
    <Header
      logo={logo}
      title="ArbiWin.bg"
      navigation={navigation}
      actions={actions}
      sticky
      className={'arbiwin-header--arbiwin ' + className}
      onHomeClick={onHomeClick}
    />
  );
};

/**
 * DashboardHeader component - Header for authenticated dashboard pages
 *
 * @param {Object} props - DashboardHeader properties
 * @param {Object} props.user - User object
 * @param {Function} props.onLogout - Logout handler
 * @param {Array} props.breadcrumbs - Breadcrumb navigation
 * @param {React.ReactNode} props.actions - Additional header actions
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} DashboardHeader component
 */
const DashboardHeader = function DashboardHeader({
  user,
  onLogout,
  breadcrumbs = [],
  actions,
  className = ''
}) {

  const navigation = [
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Calculator', href: '/calculator' },
    { label: 'Settings', href: '/settings' }
  ];

  const headerActions = (
      <div className="arbiwin-header__dashboard-actions">
        <div className="arbiwin-header__language-toggle">
          <LanguageToggle 
            variant="dropdown"
            size="medium"
            showFlags={true}
            showLabels={true}
            showCurrentLanguage={true}
            position="bottom-right"
          />
        </div>
        {actions}
      <div className="arbiwin-header__user-menu">
        <span className="arbiwin-header__user-name">
          {user?.name || user?.email}
        </span>
        <Button
          variant="secondary"
          size="small"
          onClick={onLogout}
        >
          Изход
        </Button>
      </div>
    </div>
  );

  return (
    <Header
      title="ArbiWin.bg"
      navigation={navigation}
      actions={headerActions}
      variant="compact"
      sticky
      className={'arbiwin-header--dashboard ' + className}
    />
  );
};

Header.displayName = 'Header';
ArbiWinHeader.displayName = 'ArbiWinHeader';
DashboardHeader.displayName = 'DashboardHeader';

export default Header;
export { ArbiWinHeader, DashboardHeader };
